import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@compiled/react';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box-without-terminal';
import { useDragAndDropAsyncModules } from '../async-import';
import { cssVarTableHeight } from '../constants';

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const DropTarget = styled.div({
	position: 'absolute',
	top: 0,
	left: 0,
	width: 'calc(100% + 1px)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `var(${cssVarTableHeight})`,
	display: 'none',
});

export function useFullHeightColumnDropTarget({ id, index }: { id: string; index: number }) {
	const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
	const [isDraggingAnyColumn, setIsDraggingAnyColumn] = useState(false);
	const { monitorForElements, dropTargetForElements, attachClosestEdge, extractClosestEdge } =
		useDragAndDropAsyncModules();

	const dropTargetRef = useRef<HTMLDivElement>(null);

	const dropTargetJSX = (
		<DropTarget
			ref={dropTargetRef}
			data-testid="business-list.controllers.drag-and-drop.use-full-height-column-drop-target.drop-target"
		>
			{closestEdge && <DropIndicator edge={closestEdge} />}
		</DropTarget>
	);

	useEffect(
		() =>
			monitorForElements({
				canMonitor({ source }) {
					return source.data.type === 'table-header';
				},
				onGenerateDragPreview() {
					if (dropTargetRef.current) {
						dropTargetRef.current.style.display = 'block';
					}
				},
				onDragStart() {
					setIsDraggingAnyColumn(true);
				},
				onDrop() {
					setIsDraggingAnyColumn(false);
					if (dropTargetRef.current) {
						dropTargetRef.current.style.display = 'none';
					}
				},
			}),
		[monitorForElements, setIsDraggingAnyColumn],
	);

	useEffect(() => {
		const dropTarget = dropTargetRef.current;
		if (!isDraggingAnyColumn || !dropTarget) {
			// Replace with lodash/noop
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			return () => {};
		}

		return dropTargetForElements({
			element: dropTarget,
			getIsSticky() {
				return true;
			},
			getData({ input, element }) {
				const data = { id, index };
				return attachClosestEdge(data, {
					input,
					element,
					allowedEdges: ['left', 'right'],
				});
			},
			canDrop({ source }) {
				return source.data.type === 'table-header';
			},
			onDrag({ source, self }) {
				if (source.data.id !== id) {
					setClosestEdge(extractClosestEdge(self.data));
				}
			},
			onDragLeave() {
				setClosestEdge(null);
			},
			onDrop() {
				setClosestEdge(null);
			},
		});
	}, [
		attachClosestEdge,
		dropTargetForElements,
		extractClosestEdge,
		id,
		index,
		isDraggingAnyColumn,
	]);

	return { dropTargetJSX };
}
